import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export function attachAuth(token) {
  if (token) {
    client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common["Authorization"];
  }
}

// FormData bodies (extra PDF attachments) need the browser to set the multipart boundary.
const emailOpts = (body) =>
  body instanceof FormData ? { headers: { "Content-Type": "multipart/form-data" } } : {};

export const api = {
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    client.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  activeSessions: () => client.get("/auth/sessions").then((r) => r.data),
  revokeUserSessions: (user_id) =>
    client.post("/auth/sessions/revoke", { user_id }).then((r) => r.data),
  licenceHistory: (id) => client.get(`/licences/${id}/history`).then((r) => r.data),
  listUsers: () => client.get("/auth/users").then((r) => r.data),
  createUser: (body) => client.post("/auth/users", body).then((r) => r.data),
  updateUser: (id, body) => client.patch(`/auth/users/${id}`, body).then((r) => r.data),
  deleteUser: (id) => client.delete(`/auth/users/${id}`),

  // stats
  trafficStats: () => client.get("/stats/traffic").then((r) => r.data),

  // quotations
  listQuotations: (params) => client.get("/quotations", { params }).then((r) => r.data),
  getQuotation: (id) => client.get(`/quotations/${id}`).then((r) => r.data),
  createQuotation: (body) => client.post("/quotations", body).then((r) => r.data),
  updateQuotation: (id, body) => client.patch(`/quotations/${id}`, body).then((r) => r.data),
  deleteQuotation: (id) => client.delete(`/quotations/${id}`),
  updateQuotationStatus: (id, status_value) =>
    client.patch(`/quotations/${id}/status`, { status: status_value }).then((r) => r.data),
  convertQuotation: (id) => client.post(`/quotations/${id}/convert-to-order`, {}).then((r) => r.data),
  quotationPdfUrl: (id) => `${API}/quotations/${id}/pdf`,
  openQuotationPdf: (id) => openPdfBlob(client.get(`/quotations/${id}/pdf`, { responseType: "blob" }), `quotation-${id}.pdf`),
  fetchQuotationPdfBlob: (id) => client.get(`/quotations/${id}/pdf`, { responseType: "blob" }),
  fetchQuotationPngBlob: (id) => client.get(`/quotations/${id}/pdf`, { params: { as: "png" }, responseType: "blob" }),
  dashboard: () => client.get("/dashboard").then((r) => r.data),
  clientsStats: () => client.get("/clients/stats").then((r) => r.data),

  listClients: () => client.get("/clients").then((r) => r.data),
  createClient: (body) => client.post("/clients", body).then((r) => r.data),
  updateClient: (id, body) => client.put(`/clients/${id}`, body).then((r) => r.data),
  deleteClient: (id) => client.delete(`/clients/${id}`),

  listItems: () => client.get("/items").then((r) => r.data),
  createItem: (body) => client.post("/items", body).then((r) => r.data),
  updateItem: (id, body) => client.put(`/items/${id}`, body).then((r) => r.data),
  deleteItem: (id) => client.delete(`/items/${id}`),
  importItemsCsv: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/items/import", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },

  listOrders: () => client.get("/orders").then((r) => r.data),
  getOrder: (id) => client.get(`/orders/${id}`).then((r) => r.data),
  createOrder: (body) => client.post("/orders", body).then((r) => r.data),
  updateOrder: (id, body) => client.put(`/orders/${id}`, body).then((r) => r.data),
  deleteOrder: (id) => client.delete(`/orders/${id}`),
  convertOrder: (id, body) =>
    client.post(`/orders/${id}/convert-to-delivery`, body).then((r) => r.data),

  listDeliveryOrders: () => client.get("/delivery-orders").then((r) => r.data),
  getDeliveryOrder: (id) => client.get(`/delivery-orders/${id}`).then((r) => r.data),
  updateDeliveryOrder: (id, body) => client.patch(`/delivery-orders/${id}`, body).then((r) => r.data),
  deleteDeliveryOrder: (id) => client.delete(`/delivery-orders/${id}`),
  updateDeliveryStatus: (id, status_value) =>
    client.patch(`/delivery-orders/${id}/status`, { status: status_value }).then((r) => r.data),
  updateDeliveryLineSerials: (id, payload) =>
    client.patch(`/delivery-orders/${id}/serials`, payload).then((r) => r.data),
  deliveryOrderPdfUrl: (id) => `${API}/delivery-orders/${id}/pdf`,
  openDeliveryOrderPdf: (id) => openPdfBlob(client.get(`/delivery-orders/${id}/pdf`, { responseType: "blob" }), `delivery-order-${id}.pdf`),
  fetchDeliveryOrderPdfBlob: (id) => client.get(`/delivery-orders/${id}/pdf`, { responseType: "blob" }),
  fetchDeliveryOrderPngBlob: (id) => client.get(`/delivery-orders/${id}/pdf`, { params: { as: "png" }, responseType: "blob" }),

  // client assets
  listClientAssets: (clientId) =>
    client.get(`/clients/${clientId}/assets`).then((r) => r.data),
  createClientAsset: (clientId, body) =>
    client.post(`/clients/${clientId}/assets`, body).then((r) => r.data),
  updateAsset: (assetId, body) =>
    client.patch(`/client-assets/${assetId}`, body).then((r) => r.data),
  deleteAsset: (assetId) => client.delete(`/client-assets/${assetId}`),
  listExpiringAssets: (days = 30) =>
    client.get(`/client-assets/expiring`, { params: { days } }).then((r) => r.data),

  // invoices
  listInvoices: (params) => client.get("/invoices", { params }).then((r) => r.data),
  getInvoice: (id) => client.get(`/invoices/${id}`).then((r) => r.data),
  createInvoiceFromOrder: (orderId, body) =>
    client.post(`/orders/${orderId}/create-invoice`, body).then((r) => r.data),
  createInvoiceFromDO: (doId, body) =>
    client.post(`/delivery-orders/${doId}/create-invoice`, body).then((r) => r.data),
  createInvoice: (body) => client.post("/invoices", body).then((r) => r.data),
  updateInvoice: (id, body) => client.patch(`/invoices/${id}`, body).then((r) => r.data),
  updateInvoiceStatus: (id, status_value) =>
    client.patch(`/invoices/${id}/status`, { status: status_value }).then((r) => r.data),
  deleteInvoice: (id) => client.delete(`/invoices/${id}`),
  invoicePdfUrl: (id) => `${API}/invoices/${id}/pdf`,
  openInvoicePdf: (id) => openPdfBlob(client.get(`/invoices/${id}/pdf`, { responseType: "blob" }), `invoice-${id}.pdf`),
  fetchInvoicePdfBlob: (id) => client.get(`/invoices/${id}/pdf`, { responseType: "blob" }),
  fetchInvoicePngBlob: (id) => client.get(`/invoices/${id}/pdf`, { params: { as: "png" }, responseType: "blob" }),

  // email dispatch (Gmail SMTP)
  emailInvoice: (id, body) => client.post(`/invoices/${id}/email`, body, emailOpts(body)).then((r) => r.data),
  addInvoiceLine: (id, body) => client.post(`/invoices/${id}/lines`, body).then((r) => r.data),
  deleteInvoiceLine: (id, lineIndex) =>
    client.delete(`/invoices/${id}/lines/${lineIndex}`).then((r) => r.data),
  emailQuotation: (id, body) => client.post(`/quotations/${id}/email`, body, emailOpts(body)).then((r) => r.data),
  emailDeliveryOrder: (id, body) => client.post(`/delivery-orders/${id}/email`, body, emailOpts(body)).then((r) => r.data),

  // payments
  listInvoicePayments: (id) => client.get(`/invoices/${id}/payments`).then((r) => r.data),
  recordInvoicePayment: (id, body) => client.post(`/invoices/${id}/payments`, body).then((r) => r.data),
  deleteInvoicePayment: (id, pid) => client.delete(`/invoices/${id}/payments/${pid}`),
  listAllPayments: () => client.get("/payments").then((r) => r.data),

  // reports
  reportSalesByMonth: (year, currency) => client.get("/reports/sales-by-month", { params: { year, currency } }).then((r) => r.data),
  reportSalesByQuarter: (year, currency) => client.get("/reports/sales-by-quarter", { params: { year, currency } }).then((r) => r.data),
  reportSalesByYear: (years, currency) => client.get("/reports/sales-by-year", { params: { years, currency } }).then((r) => r.data),
  reportTopClients: (limit, currency) => client.get("/reports/top-clients", { params: { limit, currency } }).then((r) => r.data),
  reportOutstanding: (currency) => client.get("/reports/outstanding", { params: { currency } }).then((r) => r.data),
  reportSellingItems: (params) => client.get("/reports/selling-items", { params }).then((r) => r.data),
  reportTaxSummary: (year, currency) => client.get("/reports/tax-summary", { params: { year, currency } }).then((r) => r.data),
  reportProfitLoss: (year, currency) => client.get("/reports/profit-loss", { params: { year, currency } }).then((r) => r.data),
  reportExpensesSummary: (year, currency) => client.get("/reports/expenses-summary", { params: { year, currency } }).then((r) => r.data),
  reportProjectProfitability: (year, currency) => client.get("/reports/project-profitability", { params: { year, currency } }).then((r) => r.data),
  fxRates: () => client.get("/fx").then((r) => r.data),

  // expenses
  listExpenses: (params = {}) =>
    client.get("/expenses", { params }).then((r) => r.data),
  createExpense: (body) => client.post("/expenses", body).then((r) => r.data),
  updateExpense: (id, body) => client.patch(`/expenses/${id}`, body).then((r) => r.data),
  deleteExpense: (id) => client.delete(`/expenses/${id}`).then((r) => r.data),
  expensesMonthly: (year) =>
    client.get("/expenses/monthly", { params: year ? { year } : {} }).then((r) => r.data),
  expensesCategories: () => client.get("/expenses/categories").then((r) => r.data),

  // loans / financing (Credit Memo — Option A)
  listLoans: (params = {}) => client.get("/loans", { params }).then((r) => r.data),
  loanDetail: (id) => client.get(`/loans/${id}`).then((r) => r.data),
  createLoan: (body) => client.post("/loans", body).then((r) => r.data),
  updateLoan: (id, body) => client.patch(`/loans/${id}`, body).then((r) => r.data),
  deleteLoan: (id) => client.delete(`/loans/${id}`).then((r) => r.data),
  addLoanRepayment: (id, body) =>
    client.post(`/loans/${id}/repayments`, body).then((r) => r.data),
  deleteLoanRepayment: (loanId, repId) =>
    client.delete(`/loans/${loanId}/repayments/${repId}`).then((r) => r.data),
  loansStats: () => client.get("/loans/stats").then((r) => r.data),

  // client attendance / time-tracking
  listAttendance: (params = {}) => client.get("/attendance", { params }).then((r) => r.data),
  createAttendance: (body) => client.post("/attendance", body).then((r) => r.data),
  updateAttendance: (id, body) => client.patch(`/attendance/${id}`, body).then((r) => r.data),
  deleteAttendance: (id) => client.delete(`/attendance/${id}`).then((r) => r.data),
  attendanceStats: (month) =>
    client.get("/attendance/stats", { params: month ? { month } : {} }).then((r) => r.data),
  openAttendancePdf: (params = {}) =>
    openPdfBlob(
      client.get("/attendance/pdf", { params, responseType: "blob" }),
      `attendance-${params.month || "month"}.pdf`,
    ),
  fetchAttendancePdfBlob: (params = {}) =>
    client.get("/attendance/pdf", { params, responseType: "blob" }),

  // engineer directory (any-staff slim user list)
  usersDirectory: () => client.get("/auth/users-directory").then((r) => r.data),
  // modules catalog for the Users & Roles permissions checklist
  modulesCatalog: () => client.get("/auth/modules-catalog").then((r) => r.data),

  // suppliers
  listSuppliers: () => client.get("/suppliers").then((r) => r.data),
  createSupplier: (body) => client.post("/suppliers", body).then((r) => r.data),
  updateSupplier: (id, body) => client.patch(`/suppliers/${id}`, body).then((r) => r.data),
  deleteSupplier: (id) => client.delete(`/suppliers/${id}`).then((r) => r.data),

  // requests for quotation (RFQ) → issued to suppliers
  listRfqs: (params = {}) => client.get("/rfqs", { params }).then((r) => r.data),
  getRfq: (id) => client.get(`/rfqs/${id}`).then((r) => r.data),
  createRfq: (body) => client.post("/rfqs", body).then((r) => r.data),
  updateRfq: (id, body) => client.patch(`/rfqs/${id}`, body).then((r) => r.data),
  deleteRfq: (id) => client.delete(`/rfqs/${id}`).then((r) => r.data),
  updateRfqStatus: (id, status_value) =>
    client.patch(`/rfqs/${id}/status`, { status: status_value }).then((r) => r.data),
  emailRfq: (id, body) => client.post(`/rfqs/${id}/email`, body).then((r) => r.data),
  openRfqPdf: (id) =>
    openPdfBlob(
      client.get(`/rfqs/${id}/pdf`, { responseType: "blob" }),
      `rfq-${id}.pdf`,
    ),
  fetchRfqPdfBlob: (id) => client.get(`/rfqs/${id}/pdf`, { responseType: "blob" }),
  fetchRfqPngBlob: (id) =>
    client.get(`/rfqs/${id}/pdf`, { params: { as: "png" }, responseType: "blob" }),
  createPoFromRfq: (id) =>
    client.post(`/rfqs/${id}/create-po`).then((r) => r.data),
  addRfqSupplier: (id, supplier_id) =>
    client.post(`/rfqs/${id}/suppliers`, { supplier_id }).then((r) => r.data),
  removeRfqSupplier: (id, supplier_id) =>
    client.delete(`/rfqs/${id}/suppliers/${supplier_id}`).then((r) => r.data),
  addRfqQuote: (id, body) =>
    client.post(`/rfqs/${id}/quotes`, body).then((r) => r.data),
  removeRfqQuote: (id, quote_id) =>
    client.delete(`/rfqs/${id}/quotes/${quote_id}`).then((r) => r.data),
  selectRfqWinner: (id, quote_id) =>
    client.post(`/rfqs/${id}/quotes/${quote_id}/winner`).then((r) => r.data),
  ensureRfqPublicLink: (id) =>
    client.post(`/rfqs/${id}/public-link`).then((r) => r.data),
  // Public (no-auth) — used by supplier RFQ portal
  getPublicRfq: (token) =>
    axios.get(`${API}/public/rfqs/${token}`).then((r) => r.data),
  submitPublicRfqQuote: (token, body) =>
    axios.post(`${API}/public/rfqs/${token}/submit`, body).then((r) => r.data),
  publicRfqPdfUrl: (token) => `${API}/public/rfqs/${token}/pdf`,
  broadcastRfqPortal: (id, base_url) =>
    client.post(`/rfqs/${id}/broadcast-portal`, { base_url }).then((r) => r.data),
  fetchRfqComparePdfBlob: (id) =>
    client.get(`/rfqs/${id}/compare.pdf`, { responseType: "blob" }),
  downloadRfqCompareCsv: (id) =>
    client.get(`/rfqs/${id}/compare.csv`, { responseType: "blob" }),
  createRfqFromQuotation: (quotation_id, body) =>
    client.post(`/quotations/${quotation_id}/create-rfq`, body).then((r) => r.data),

  // Reminder settings (Reminder Center)
  getReminderSettings: () => client.get("/system/reminder-settings").then((r) => r.data),
  updateReminderSettings: (body) =>
    client.put("/system/reminder-settings", body).then((r) => r.data),

  // Winner approval
  approveRfqWinner: (id, body) =>
    client.post(`/rfqs/${id}/approve-winner`, body).then((r) => r.data),
  revokeRfqWinnerApproval: (id) =>
    client.delete(`/rfqs/${id}/approve-winner/revoke`).then((r) => r.data),

  // Portal chat (RFQ supplier Q&A)
  postRfqStaffMessage: (id, text) =>
    client.post(`/rfqs/${id}/messages`, { text }).then((r) => r.data),
  getPublicRfqMessages: (token) =>
    axios.get(`${API}/public/rfqs/${token}/messages`).then((r) => r.data),
  postPublicRfqMessage: (token, body) =>
    axios.post(`${API}/public/rfqs/${token}/message`, body).then((r) => r.data),

  // Reminder test send
  sendReminderTest: (kind) =>
    client.post("/system/reminder-settings/test-send", { kind }).then((r) => r.data),

  // Supplier bid analytics (for Reports page)
  rfqSupplierAnalytics: (params = {}) =>
    client.get("/rfqs/supplier-analytics", { params }).then((r) => r.data),
  rfqSupplierAnalyticsCsvUrl: () => `${API}/rfqs/supplier-analytics.csv`,
  downloadRfqSupplierAnalyticsCsv: async (params = {}) => {
    const res = await client.get(`/rfqs/supplier-analytics.csv`, {
      params,
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kinos-supplier-bids.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  rfqDashboardOpen: () =>
    client.get("/rfqs/dashboard-open").then((r) => r.data),
  sendBestBidDigestNow: () =>
    client.post("/rfqs/best-bid-digest/send-now").then((r) => r.data),

  // purchase orders
  listPurchaseOrders: (params = {}) => client.get("/purchase-orders", { params }).then((r) => r.data),
  getPurchaseOrder: (id) => client.get(`/purchase-orders/${id}`).then((r) => r.data),
  createPurchaseOrder: (body) => client.post("/purchase-orders", body).then((r) => r.data),
  updatePurchaseOrder: (id, body) =>
    client.patch(`/purchase-orders/${id}`, body).then((r) => r.data),
  deletePurchaseOrder: (id) => client.delete(`/purchase-orders/${id}`).then((r) => r.data),
  updatePurchaseOrderStatus: (id, status_value) =>
    client.patch(`/purchase-orders/${id}/status`, { status: status_value }).then((r) => r.data),
  emailPurchaseOrder: (id, body = {}) =>
    client.post(`/purchase-orders/${id}/email`, body).then((r) => r.data),
  approvePurchaseOrder: (id, body) =>
    client.post(`/purchase-orders/${id}/approve`, body).then((r) => r.data),
  createPurchaseOrderFromQuotation: (body) =>
    client.post(`/purchase-orders/from-quotation`, body).then((r) => r.data),
  // client statements
  openClientStatementPdf: (clientId) =>
    openPdfBlob(
      client.get(`/clients/${clientId}/statement/pdf`, { responseType: "blob" }),
      "statement.pdf",
    ),
  emailClientStatement: (clientId, body = {}) =>
    client.post(`/clients/${clientId}/statement/email`, body).then((r) => r.data),
  // public (no-auth) — supplier PO confirmation page
  getPublicPo: (token) =>
    axios.get(`${API}/public/po/${token}`).then((r) => r.data),
  confirmPublicPo: (token, body) =>
    axios.post(`${API}/public/po/${token}/confirm`, body).then((r) => r.data),
  publicPoPdfUrl: (token) => `${API}/public/po/${token}/pdf`,
  openPurchaseOrderPdf: (id) =>
    openPdfBlob(
      client.get(`/purchase-orders/${id}/pdf`, { responseType: "blob" }),
      `purchase-order-${id}.pdf`,
    ),

  // company assets — fixed-asset register
  listAssets: (params = {}) => client.get("/assets", { params }).then((r) => r.data),
  listStockTakes: () => client.get("/assets/stock-takes").then((r) => r.data),
  getStockTake: (id) => client.get(`/assets/stock-takes/${id}`).then((r) => r.data),
  openStockTakePdf: (id) =>
    openPdfBlob(client.get(`/assets/stock-takes/${id}/pdf`, { responseType: "blob" }), "stock-take.pdf"),
  finishStockTake: (body) => client.post("/assets/stock-takes", body).then((r) => r.data),
  deleteStockTake: (id) => client.delete(`/assets/stock-takes/${id}`),

  // software spend chart
  licenceSpendChart: () => client.get("/licences/spend-chart").then((r) => r.data),
  licenceBudgetStatus: () => client.get("/licences/budget-status").then((r) => r.data),

  // client-reported payments ("I've paid" link)
  getPublicPay: (token) => axios.get(`${API}/public/pay/${token}`).then((r) => r.data),
  submitPublicPayClaim: (token, formData) =>
    axios.post(`${API}/public/pay/${token}/claim`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data),
  listPaymentClaims: (params = {}) =>
    client.get("/payment-claims", { params }).then((r) => r.data),
  paymentClaimsSummary: () => client.get("/payment-claims/summary").then((r) => r.data),
  bulkAcceptPaymentClaims: (ids, body = {}) =>
    client.post("/payment-claims/bulk-accept", { ids, ...body }).then((r) => r.data),
  acceptPaymentClaim: (id, body = {}) =>
    client.post(`/payment-claims/${id}/accept`, body).then((r) => r.data),
  dismissPaymentClaim: (id, body = {}) =>
    client.post(`/payment-claims/${id}/dismiss`, body).then((r) => r.data),
  openPaymentClaimProof: (id) =>
    client.get(`/payment-claims/${id}/proof`, { responseType: "blob" })
      .then((r) => window.open(URL.createObjectURL(r.data), "_blank", "noopener")),
  getClientPayLink: (clientId) =>
    client.post(`/clients/${clientId}/pay-link`).then((r) => r.data),
  resetClientPayLink: (clientId) =>
    client.post(`/clients/${clientId}/pay-link/reset`).then((r) => r.data),

  // bank accounts (Settings)
  listBankAccounts: () => client.get("/bank-accounts").then((r) => r.data),

  // project funds (Financing)
  listProjectFunds: (params = {}) => client.get("/project-funds", { params }).then((r) => r.data),
  projectFundsCashSummary: () => client.get("/project-funds/cash-summary").then((r) => r.data),
  createProjectFund: (body) => client.post("/project-funds", body).then((r) => r.data),
  updateProjectFund: (id, body) => client.patch(`/project-funds/${id}`, body).then((r) => r.data),
  deleteProjectFund: (id) => client.delete(`/project-funds/${id}`),
  addProjectFundEntry: (id, body) => client.post(`/project-funds/${id}/entries`, body).then((r) => r.data),
  deleteProjectFundEntry: (id, entryId) =>
    client.delete(`/project-funds/${id}/entries/${entryId}`).then((r) => r.data),

  // leave requests
  listLeave: (params = {}) => client.get("/leave", { params }).then((r) => r.data),
  createLeave: (body) => client.post("/leave", body).then((r) => r.data),
  cancelLeave: (id) => client.post(`/leave/${id}/cancel`).then((r) => r.data),
  decideLeave: (id, body) => client.post(`/leave/${id}/decide`, body).then((r) => r.data),
  deleteLeave: (id) => client.delete(`/leave/${id}`),
  leaveCalendar: (month) => client.get("/leave/calendar", { params: { month } }).then((r) => r.data),
  leavePendingCount: () => client.get("/leave/pending-count").then((r) => r.data),
  leaveTeamBalances: (year) => client.get("/leave/balances", { params: { year } }).then((r) => r.data),
  uploadLeaveAttachment: (id, file) => { const fd = new FormData(); fd.append("file", file); return client.post(`/leave/${id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  deleteLeaveAttachment: (id) => client.delete(`/leave/${id}/attachment`).then((r) => r.data),
  openLeaveAttachment: (id) => client.get(`/leave/${id}/attachment`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank", "noopener")),
  myPayslips: () => client.get("/leave/my-payslips").then((r) => r.data),
  openMyPayslipPdf: (runId, month) => openPdfBlob(client.get(`/leave/my-payslips/${runId}/pdf`, { responseType: "blob" }), `payslip-${month}.pdf`),
  openMyYearSummaryPdf: (year) => openPdfBlob(client.get("/leave/my-year-summary-pdf", { params: { year }, responseType: "blob" }), `earnings-summary-${year}.pdf`),
  uploadExpenseReceipt: (id, file) => { const fd = new FormData(); fd.append("file", file); return client.post(`/expenses/${id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  deleteExpenseReceipt: (id) => client.delete(`/expenses/${id}/receipt`).then((r) => r.data),
  openExpenseReceipt: (id) => client.get(`/expenses/${id}/receipt`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank", "noopener")),
  openFundEntryReceipt: (fundId, entryId) => client.get(`/project-funds/${fundId}/entries/${entryId}/receipt`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank", "noopener")),
 // Rent & Bills
  listBills: (params = {}) => client.get("/bills", { params }).then((r) => r.data),
  createBill: (body) => client.post("/bills", body).then((r) => r.data),
  getBill: (id) => client.get(`/bills/${id}`).then((r) => r.data),
  updateBill: (id, body) => client.patch(`/bills/${id}`, body).then((r) => r.data),
  deleteBill: (id, force = false) => client.delete(`/bills/${id}`, { params: force ? { force: "true" } : {} }),
  billsSummary: (days = 7) => client.get("/bills/summary", { params: { days } }).then((r) => r.data),
  billsYearly: (year) => client.get("/bills/yearly", { params: { year } }).then((r) => r.data),
  openBillsStatementPdf: (year) => openPdfBlob(client.get("/bills/statement-pdf", { params: { year }, responseType: "blob" }), `rent-utilities-statement-${year}.pdf`),
  addBillPayment: (billId, body) => client.post(`/bills/${billId}/payments`, body).then((r) => r.data),
  updateBillPayment: (billId, payId, body) => client.patch(`/bills/${billId}/payments/${payId}`, body).then((r) => r.data),
  deleteBillPayment: (billId, payId) => client.delete(`/bills/${billId}/payments/${payId}`),
  uploadBillReceipt: (billId, payId, file) => { const fd = new FormData(); fd.append("file", file); return client.post(`/bills/${billId}/payments/${payId}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  openBillReceipt: (billId, payId) => client.get(`/bills/${billId}/payments/${payId}/receipt`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank", "noopener")),
  emailBillConfirmation: (billId, payId, body = {}) => client.post(`/bills/${billId}/payments/${payId}/confirmation-email`, body).then((r) => r.data),
  acknowledgeMyPayslip: (runId) => client.post(`/payroll/my-payslips/${runId}/acknowledge`).then((r) => r.data),
  runBillReminders: () => client.post("/bills/reminders/run-now").then((r) => r.data),
  sendFundDigestNow: () => client.post("/project-funds/digest/send-now").then((r) => r.data),
  openFundDigestPdf: () => openPdfBlob(client.get("/project-funds/digest/pdf", { responseType: "blob" }), "project-funds-digest.pdf"),
  downloadHolidaysIcs: (year) =>
    client.get("/holidays/ics", { params: { year }, responseType: "blob" }).then((r) => saveBlob(r.data, `kinos-public-holidays-${year}.ics`)),
  downloadPayrollArchive: (year) =>
    client.get("/payroll/archive", { params: { year }, responseType: "blob" }).then((r) => saveBlob(r.data, `kinos-payroll-archive-${year}.zip`)),

  // public holidays
  listHolidays: (year) => client.get("/holidays", { params: year ? { year } : {} }).then((r) => r.data),
  createHoliday: (body) => client.post("/holidays", body).then((r) => r.data),
  updateHoliday: (id, body) => client.put(`/holidays/${id}`, body).then((r) => r.data),
  deleteHoliday: (id) => client.delete(`/holidays/${id}`),
  seedHolidays: (year) => client.post("/holidays/seed", { year }).then((r) => r.data),
  seedMovableHolidays: (year) => client.post("/holidays/seed-movable", { year }).then((r) => r.data),
  runHolidayReminders: () => client.post("/holidays/reminders/run-now").then((r) => r.data),
  emailEmployeeYearSummary: (employeeId, year) => client.post(`/payroll/employees/${employeeId}/year-summary-email`, { year }).then((r) => r.data),
  emailAllYearSummaries: (year) => client.post("/payroll/year-summary-email", { year }).then((r) => r.data),

  // project fund options for expense tagging
  projectFundOptions: () => client.get("/project-funds/options").then((r) => r.data),
  openEmployeeYearSummaryPdf: (employeeId, year) =>
    openPdfBlob(client.get(`/payroll/employees/${employeeId}/year-summary-pdf`, { params: { year }, responseType: "blob" }), `earnings-summary-${year}.pdf`),

  // payroll (admin)
  listEmployees: () => client.get("/payroll/employees").then((r) => r.data),
  createEmployee: (body) => client.post("/payroll/employees", body).then((r) => r.data),
  updateEmployee: (id, body) => client.patch(`/payroll/employees/${id}`, body).then((r) => r.data),
  deleteEmployee: (id) => client.delete(`/payroll/employees/${id}`),
  listPayrollRuns: () => client.get("/payroll/runs").then((r) => r.data),
  createPayrollRun: (month) => client.post("/payroll/runs", { month }).then((r) => r.data),
  getPayrollRun: (id) => client.get(`/payroll/runs/${id}`).then((r) => r.data),
  deletePayrollRun: (id) => client.delete(`/payroll/runs/${id}`),
  updatePayslip: (runId, slipId, body) =>
    client.patch(`/payroll/runs/${runId}/slips/${slipId}`, body).then((r) => r.data),
  finalizePayrollRun: (id) => client.post(`/payroll/runs/${id}/finalize`).then((r) => r.data),
  emailPayrollRun: (id, body = {}) => client.post(`/payroll/runs/${id}/email`, body).then((r) => r.data),
  emailPayslip: (runId, slipId) => client.post(`/payroll/runs/${runId}/slips/${slipId}/email`).then((r) => r.data),
  openInssReportPdf: (runId) =>
    openPdfBlob(client.get(`/payroll/runs/${runId}/inss-pdf`, { responseType: "blob" }), "inss-statement.pdf"),
  downloadPayrollBankCsv: (runId, month) =>
    client.get(`/payroll/runs/${runId}/bank-csv`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `salary-transfer-${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }),
  openPayslipPdf: (runId, slipId) =>
    openPdfBlob(client.get(`/payroll/runs/${runId}/slips/${slipId}/pdf`, { responseType: "blob" }), "payslip.pdf"),
  createBankAccount: (body) => client.post("/bank-accounts", body).then((r) => r.data),
  updateBankAccount: (id, body) => client.put(`/bank-accounts/${id}`, body).then((r) => r.data),
  deleteBankAccount: (id) => client.delete(`/bank-accounts/${id}`),
  reorderBankAccounts: (ids) => client.post("/bank-accounts/reorder", { ids }).then((r) => r.data),
  createAsset: (body) => client.post("/assets", body).then((r) => r.data),
  updateCompanyAsset: (id, body) => client.patch(`/assets/${id}`, body).then((r) => r.data),
  deleteCompanyAsset: (id) => client.delete(`/assets/${id}`).then((r) => r.data),
  uploadAssetPhoto: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/assets/${id}/photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  getAssetPhotoBlobUrl: (id) =>
    client.get(`/assets/${id}/photo`, { responseType: "blob" })
      .then((r) => URL.createObjectURL(r.data)),
  deleteAssetPhoto: (id) => client.delete(`/assets/${id}/photo`).then((r) => r.data),
  openAssetQrLabelPdf: (id) =>
    openPdfBlob(
      client.get(`/assets/${id}/qr-label/pdf`, { responseType: "blob" }),
      "asset-qr-label.pdf",
    ),
  openAssetQrLabelsPdf: (params = {}) =>
    openPdfBlob(
      client.get("/assets/qr-labels/pdf", { params, responseType: "blob" }),
      "asset-qr-labels.pdf",
    ),
  openAssetRegisterPdf: (params = {}) =>
    openPdfBlob(
      client.get("/assets/register/pdf", { params, responseType: "blob" }),
      "asset-register.pdf",
    ),
  exportReportPdf: (payload, filename = "report.pdf") =>
    openPdfBlob(
      client.post("/reports/export-pdf", payload, { responseType: "blob" }),
      filename,
    ),

  staffLeaderboard: (period = "week") =>
    client.get("/reports/staff-leaderboard", { params: { period } }).then((r) => r.data),
  listStaffTargets: () => client.get("/staff-targets").then((r) => r.data),
  getStaffTarget: (userId) => client.get(`/staff-targets/${userId}`).then((r) => r.data),
  setStaffTarget: (userId, body) =>
    client.put(`/staff-targets/${userId}`, body).then((r) => r.data),
  deleteStaffTarget: (userId) =>
    client.delete(`/staff-targets/${userId}`).then((r) => r.data),

  // audit / activity log
  listAuditLogs: (params = {}) =>
    client.get("/audit-logs", { params }).then((r) => r.data),

  // Software Licences — track keys/seats/expiry with renewal auto-nudge
  listLicences: (params = {}) =>
    client.get("/licences", { params }).then((r) => r.data),
  createLicence: (body) => client.post("/licences", body).then((r) => r.data),
  getLicence: (id) => client.get(`/licences/${id}`).then((r) => r.data),
  updateLicence: (id, body) =>
    client.patch(`/licences/${id}`, body).then((r) => r.data),
  deleteLicence: (id) =>
    client.delete(`/licences/${id}`).then((r) => r.data),
  licenceDashboardSummary: () =>
    client.get("/licences/dashboard-summary").then((r) => r.data),
  uploadLicenceAttachment: (id, file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post(`/licences/${id}/attachment`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  deleteLicenceAttachment: (id) =>
    client.delete(`/licences/${id}/attachment/delete`).then((r) => r.data),
  downloadLicenceAttachment: async (id, filename) => {
    const res = await client.get(`/licences/${id}/attachment/download`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `licence-${id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  // public (no-auth) — used by the online quotation approval page
  getPublicQuotation: (token) =>
    axios.get(`${API}/public/quotations/${token}`).then((r) => r.data),
  decidePublicQuotation: (token, body) =>
    axios.post(`${API}/public/quotations/${token}/decide`, body).then((r) => r.data),
  publicQuotationPdfUrl: (token) => `${API}/public/quotations/${token}/pdf`,

  // system — backup & restore
  createBackup: () =>
    client.post("/system/backup", null, { responseType: "blob" }),
  restoreBackup: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/system/backup/restore", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600_000,
    }).then((r) => r.data);
  },
  listScheduledBackups: () =>
    client.get("/system/backup/scheduled").then((r) => r.data),
  getBackupStatus: () =>
    client.get("/system/backup/status").then((r) => r.data),
  getBackupSetupCheck: () =>
    client.get("/system/backup/setup-check").then((r) => r.data),
  downloadScheduledBackup: (filename) =>
    client.get(`/system/backup/scheduled/${encodeURIComponent(filename)}`, {
      responseType: "blob",
    }),
  deleteScheduledBackup: (filename) =>
    client.delete(`/system/backup/scheduled/${encodeURIComponent(filename)}/delete`),
  previewDigest: () =>
    client.get("/system/digest/preview").then((r) => r.data),
  openOutstandingPdf: () =>
    openPdfBlob(
      client.get("/system/digest/pdf", { responseType: "blob" }),
      "outstanding-invoices.pdf",
    ),
  sendDigestNow: (to) =>
    client.post("/system/digest/send-now", to ? { to } : {}).then((r) => r.data),
  runStatementsNow: () =>
    client.post("/system/statements/run-now").then((r) => r.data),
  // Software renewals
  listExpiringSoftware: (days = 30) =>
    client.get(`/software/expiring?days=${days}`).then((r) => r.data),
  sendSoftwareReminderNow: (body = {}) =>
    client.post("/software/reminder/send-now", body).then((r) => r.data),
  createSoftwareRenewalQuotation: (itemId, body) =>
    client.post(`/software/${itemId}/renewal-quotation`, body).then((r) => r.data),
  ensureQuotationPublicLink: (id) =>
    client.post(`/quotations/${id}/public-link`).then((r) => r.data),
  ensureClientPortalLink: (id) =>
    client.post(`/clients/${id}/portal-link`).then((r) => r.data),
  revokeClientPortalLink: (id) =>
    client.post(`/clients/${id}/portal-link/revoke`).then((r) => r.data),
  setClientPortalSecurity: (id, login_required) =>
    client.patch(`/clients/${id}/portal-security`, { login_required }).then((r) => r.data),
  getPublicClientPortal: (token, session) =>
    axios.get(`${API}/public/portal/${token}`, {
      headers: session ? { "X-Portal-Session": session } : {},
    }).then((r) => r.data),
  requestPortalCode: (token, email) =>
    axios.post(`${API}/public/portal/${token}/request-code`, { email }).then((r) => r.data),
  verifyPortalCode: (token, email, code) =>
    axios.post(`${API}/public/portal/${token}/verify-code`, { email, code }).then((r) => r.data),
  downloadPortalInvoicesZip: async (token, scope, session) => {
    const res = await axios.get(`${API}/public/portal/${token}/invoices.zip`, {
      params: { scope },
      headers: session ? { "X-Portal-Session": session } : {},
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `KINOS-invoices-${scope}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  createLicencePo: (id) =>
    client.post(`/licences/${id}/create-po`, {}).then((r) => r.data),
  publicInvoicePdfUrl: (token) => `${API}/public/invoices/${token}/pdf`,
};

/**
 * Fetches a PDF response (as Blob) via the auth-attached axios client and
 * opens it in a new browser tab. Necessary because PDF endpoints require
 * `Authorization: Bearer <token>` which cannot be sent via plain <a href>.
 */
function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}


async function openPdfBlob(promise, filename) {
  const res = await promise;
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  // Fallback: if popup blocked, trigger a download instead.
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Release the object URL after a short delay so the new tab can load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default api;
